import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, FormInstance, Input, message, Modal, notification, Progress, TableProps } from "antd";
import useSWR from "swr";
import axios from "axios";
import dayjs from 'dayjs';
import { useLocation, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

interface DataType {
    key: React.Key;
    pr_no: string;
    so_no: string;
    item_code: string;
    item_name: string;
    uom: string;
    uom_rate: number;
    qty: number;
    price: number;
    discount: number;
    discount_amount: number;
    total: number;
    pr_line?: number;
    warehouse?: string;
    remark?: string;
}

interface Warehouse {
    warehouse_code: string;
    warehouse_name: string;
    address: string;
    contact_person: string;
}

interface Supplier {
    key: string;
    supplierCode: string;
    supplierName: string;
    address: string;
    status: string;
    discount: number;
    phone: string;
}

const fetcher = (url: string) => axios.get(url).then(res => res.data);
type AutosaveStatus = 'idle' | 'processing' | 'error' | 'stopped';
const SubmitConfirmContent: React.FC<{ onChange: (val: string) => void }> = ({ onChange }) => {
    const [value, setValue] = useState("Purchase Order ");
    useEffect(() => {
        onChange("Purchase Order ");
    }, [onChange]);
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        let val = e.target.value;
        if (!val.startsWith("Purchase Order")) {
            val = "Purchase Order";
        }
        setValue(val);
        onChange(val);
    };
    return <Input.TextArea rows={3} value={value} onChange={handleChange} />;
};
const withPurchaseOrderLogic = (WrappedComponent: React.ComponentType<any>) => {
    const EnhancedComponent = (props: any) => {
        const { userData } = useAuth();
        const location = useLocation();
        const poNo = location.state?.poNo;
        const [form] = Form.useForm();
        const [isModalVisible, setIsModalVisible] = useState(false);
        const [dataSource, setDataSource] = useState<DataType[]>([]);
        const [modalSelectedKeys, setModalSelectedKeys] = useState<React.Key[]>([]);
        const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
        const [orderTypeForModal, setOrderTypeForModal] = useState<string | null>(null);
        const [warehouseCodeForModal, setWarehouseCodeForModal] = useState<string | null>(null);
        const [isSupplierModalVisible, setIsSupplierModalVisible] = useState(false);
        const [isEditModalVisible, setIsEditModalVisible] = useState(false);
        const [editingRecord, setEditingRecord] = useState<DataType | null>(null);
        const [statusCode, setStatusCode] = useState<string | null>(null);
        const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('stopped');
        const [isPrinting, setIsPrinting] = useState(false);
        const [isAutosaveActive, setIsAutosaveActive] = useState(false);
        const autosaveTimer = useRef<NodeJS.Timeout | null>(null);

        const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
            console.log("selectedRowKeys changed: ", newSelectedRowKeys);
            setSelectedRowKeys(newSelectedRowKeys);
        };

        const rowSelection = {
            selectedRowKeys,
            onChange: onSelectChange,
        };

        const { data: brandData, error: brandError } = useSWR<{ value: string; label: string }[]>(`${process.env.REACT_APP_BACKEND_URL}/api/po/brands`, fetcher);
        const { data: profitCenterData, error: profitCenterError } = useSWR<{ value: string; label: string }[]>(`${process.env.REACT_APP_BACKEND_URL}/api/po/profit-centers`, fetcher);
        const { data: orderTypeData, error: orderTypeError } = useSWR<{ value: string; label: string }[]>(`${process.env.REACT_APP_BACKEND_URL}/api/po/order-types`, fetcher);
        const { data: topCodeData, error: topCodeError } = useSWR<{ value: string; label: string }[]>(`${process.env.REACT_APP_BACKEND_URL}/api/po/top-codes`, fetcher);
        const { data: warehouseData, error: warehouseError } = useSWR<Warehouse[]>(`${process.env.REACT_APP_BACKEND_URL}/api/po/warehouses`, fetcher);
        const { data: ppnData } = useSWR<{ value_ppn: number }>(`${process.env.REACT_APP_BACKEND_URL}/api/po/ppn-value`, fetcher);


        useEffect(() => {
            const loadPurchaseOrderData = async (poNumber: string) => {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/po/purchase-order/${poNumber}`);
                    const { header, details } = response.data;
                    setStatusCode(header.status);
                    let statusLabel;
                    switch (header.status) {
                        case '1':
                            statusLabel = 'Draft';
                            break;
                        case '2':
                            statusLabel = 'Approved';
                            break;
                        case '3':
                            statusLabel = 'Waiting for Approved';
                            break;
                        case '4':
                            statusLabel = 'Complete';
                            break;
                        case '5':
                            statusLabel = 'Cancel';
                            break;
                        default:
                            statusLabel = header.status;
                    }

                    const formattedHeader = {
                        ...header,
                        warehouse: header.warehouse_code,
                        status: statusLabel,
                        po_date: header.po_date ? dayjs(header.po_date) : null,
                        request_arrival_date: header.request_arrival_date ? dayjs(header.request_arrival_date) : null,
                        use_discount: header.use_discount === '1',
                        vat_amount: header.vat,
                        total_amount: header.total,
                    };

                    const parsedDetails = details.map((d: any) => {
                        const price = parseFloat(d.price) || 0;
                        const qty = parseFloat(d.qty) || 0;
                        const discountAmount = parseFloat(d.discount) || 0;
                        const subTotal = price * qty;
                        const discountPercentage = subTotal > 0 ? (discountAmount / subTotal) * 100 : 0;

                        return {
                            ...d,
                            qty: qty,
                            price: price,
                            discount: discountPercentage,
                            discount_amount: discountAmount,
                            total: parseFloat(d.total) || 0,
                        };
                    });

                    form.setFieldsValue(formattedHeader);
                    setDataSource(parsedDetails);

                    if (header.status === '1') {
                        setIsAutosaveActive(true);
                        setAutosaveStatus('idle');
                    } else {
                        setIsAutosaveActive(false);
                        setAutosaveStatus('stopped');
                    }

                } catch (error) {
                    console.error("Gagal memuat data Purchase Order:", error);
                    notification.error({
                        message: 'Gagal Memuat Data',
                        description: `Tidak dapat mengambil data untuk PO No: ${poNumber}.`,
                    });
                }
            };

            if (poNo) {
                loadPurchaseOrderData(poNo);
            } else {
                form.resetFields();
                setDataSource([]);
                form.setFieldsValue({ status: "Draft" });
                setStatusCode('1');
                setIsAutosaveActive(false);
                setAutosaveStatus('stopped');
            }
        }, [poNo, form]);

        const watchedValues = Form.useWatch([], form);
        const isInitialMount = useRef(true);

        const performAutosave = useCallback(async () => {
            const po_no = form.getFieldValue('po_no');
            const status = form.getFieldValue('status');

            if (status !== 'Draft') {
                if (status) {
                    setAutosaveStatus('stopped');
                    console.log("Autosave dihentikan: Status dokumen bukan 'Draft'.");
                    return;
                }
            }
            if (!po_no) {
                setAutosaveStatus('stopped');
                console.log("Autosave dihentikan: Nomor PO belum ada.");
                return;
            }

            setAutosaveStatus('processing');
            try {
                const allValues = form.getFieldsValue(true);
                const formatDate = (date: any) => date ? date.format('YYYY-MM-DD') : null;
                let statusCode = '1';
                if (status === 'Draft') {
                    statusCode = '1';
                } else if (status === 'Approved') {
                    statusCode = '2';
                } else if (status === 'Waiting for Approved') {
                    statusCode = '3';
                } else if (status === 'Complete') {
                    statusCode = '4';
                }
                const po_header = {
                    ...allValues,
                    po_no: po_no,
                    po_date: formatDate(allValues.po_date) || new Date().toISOString().split('T')[0],
                    request_arrival_date: formatDate(allValues.request_arrival_date),
                    use_discount: allValues.use_discount ? '1' : '0',
                    status: statusCode,
                    vat: allValues.vat_amount,
                    total: allValues.total_amount,
                };
                delete po_header.vat_amount;
                delete po_header.total_amount;

                const po_details = dataSource.map(item => ({
                    pr_no: item.pr_no,
                    so_no: item.so_no,
                    item_code: item.item_code,
                    item_name: item.item_name,
                    uom: item.uom,
                    uom_rate: item.uom_rate,
                    qty: item.qty,
                    price: item.price,
                    discount: item.discount_amount,
                    total: item.total,
                    warehouse: item.warehouse,
                    remark: item.remark,
                }));

                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/po/autosave`, {
                    po_header,
                    po_details,
                });

                setAutosaveStatus('idle');
            } catch (error) {
                console.error("Autosave gagal:", error);
                setAutosaveStatus('error');
                notification.error({
                    message: 'Autosave Gagal',
                    description: 'Gagal menyimpan perubahan secara otomatis. Periksa koneksi Anda.',
                });
            }
        }, [form, dataSource]);

        useEffect(() => {

            if (isInitialMount.current) {
                isInitialMount.current = false;
                return;
            }


            if (isAutosaveActive) {

                if (autosaveTimer.current) {
                    clearTimeout(autosaveTimer.current);
                }

                autosaveTimer.current = setTimeout(() => {
                    performAutosave();
                }, 1500);
            }


            return () => {
                if (autosaveTimer.current) {
                    clearTimeout(autosaveTimer.current);
                }
            };

        }, [watchedValues, dataSource, isAutosaveActive, performAutosave]);

        const showModal = () => {
            const orderType = form.getFieldValue('order_type');
            const dealerRepCode = form.getFieldValue('dealer_rep_code');
            if (!orderType) {
                notification.error({ message: "Silakan pilih Order Type terlebih dahulu.", duration: 4 });
                return;
            }
            if (!dealerRepCode) {
                notification.error({ message: "Silakan pilih Warehouse terlebih dahulu.", duration: 4 });
                return;
            }
            setOrderTypeForModal(orderType);
            setWarehouseCodeForModal(dealerRepCode);
            setIsModalVisible(true);
        };

        const handleImport = async () => {
            const dealerRepCode = form.getFieldValue('dealer_rep_code');
            if (!dealerRepCode) {
                notification.error({ message: "Silakan pilih Warehouse terlebih dahulu.", duration: 4 });
                return;
            }
            if (modalSelectedKeys.length === 0) {
                notification.warning({ message: "Tidak ada data Purchase Request yang dipilih.", duration: 4 });
                return;
            }

            try {
                const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/po/purchase-request-details`, {
                    prNos: modalSelectedKeys
                });

                const newItems = response.data.map((item: any) => {
                    const price = parseFloat(item.price) || 0;
                    const qty = item.qty || 0;
                    return {
                        key: `${item.pr_no}-${item.item_code}`,
                        pr_no: item.pr_no,
                        so_no: item.so_no,
                        item_code: item.item_code,
                        item_name: item.item_name,
                        uom: item.uom,
                        uom_rate: item.uom_rate,
                        qty: qty,
                        price: price,
                        discount: 0,
                        discount_amount: 0,
                        total: price * qty,
                        warehouse: item.warehouse,
                    };
                });

                const existingKeys = new Set(dataSource.map(d => d.key));
                const uniqueNewItems = newItems.filter((item: DataType) => !existingKeys.has(item.key));

                setDataSource(prevData => [...prevData, ...uniqueNewItems]);
                setIsModalVisible(false);
                setModalSelectedKeys([]);
                notification.success({ message: "Data berhasil diimpor.", duration: 4 });
            } catch (error) {
                console.error("Failed to import PR details:", error);
                notification.error({ message: "Gagal mengimpor data Purchase Request.", duration: 4 });
            }
        };


        const handleCancel = () => {
            setIsModalVisible(false);
            setOrderTypeForModal(null);
            setWarehouseCodeForModal(null);
            setModalSelectedKeys([]);
        };

        const showSupplierModal = () => {
            setIsSupplierModalVisible(true);
        };

        const handleSupplierSelect = (supplier: Supplier) => {
            form.setFieldsValue({
                supplier_code: supplier.supplierCode,
                supplier_name: supplier.supplierName,
                supplier_address: supplier.address,
                supplier_contact_person: supplier.phone,
                discount: supplier.discount,
            });
            setIsSupplierModalVisible(false);
        };

        const handleSupplierModalCancel = () => {
            setIsSupplierModalVisible(false);
        };

        const handleRowDoubleClick = (record: DataType) => {
            const index = dataSource.findIndex(item => item.key === record.key);
            setEditingRecord({ ...record, pr_line: index + 1 });
            setIsEditModalVisible(true);
        };

        const handleEditModalCancel = () => {
            setIsEditModalVisible(false);
            setEditingRecord(null);
        };

        const handleEditModalOk = (values: any) => {
            const updatedDataSource = dataSource.map(item => {
                if (item.key === editingRecord?.key) {
                    return {
                        ...item,
                        ...values,
                    };
                }
                return item;
            });
            setDataSource(updatedDataSource);
            handleEditModalCancel();
            notification.success({ message: "Data berhasil diperbarui.", duration: 4 });
        };

        const handleWarehouseChange = (value: string) => {
            const warehouseOptions = warehouseData || [];
            const selectedWarehouse = warehouseOptions.find((wh: any) => wh.warehouse_code === value);
            if (selectedWarehouse) {
                form.setFieldsValue({
                    warehouse_code: selectedWarehouse.warehouse_code,
                    warehouse_name: selectedWarehouse.warehouse_name,
                    warehouse_address: selectedWarehouse.address,
                    warehouse_contact_person: selectedWarehouse.contact_person,
                });
            } else {
                form.setFieldsValue({
                    warehouse_code: null,
                    warehouse_name: null,
                    warehouse_address: null,
                    warehouse_contact_person: null,
                });
            }
        };

        useEffect(() => {
            if (!poNo && userData?.warehouse_code && warehouseData) {
                if (!form.getFieldValue('warehouse')) {
                    form.setFieldsValue({ warehouse: userData.warehouse_code });
                    handleWarehouseChange(userData.warehouse_code);
                }
                if (!form.getFieldValue('dealer_rep_code')) {
                    form.setFieldsValue({ dealer_rep_code: userData.warehouse_code });
                }
            }
        }, [poNo, userData, warehouseData, form, handleWarehouseChange]);

        const handleOrderTypeChange = async (value: string, form: FormInstance) => {
            if (value && !poNo) {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/po/generate-po-no`);
                    const { po_no } = response.data;
                    form.setFieldsValue({ po_no: po_no, po_date: dayjs() });
                    setIsAutosaveActive(true);
                    setAutosaveStatus('idle');
                } catch (error) {
                    console.error("Failed to generate PO number:", error);
                    notification.error({ message: "Gagal menghasilkan nomor PO.", duration: 4 });
                }
            } else {
                form.setFieldsValue({ po_no: null });
                setIsAutosaveActive(false);
                setAutosaveStatus('stopped');
            }
        };

        const handleDelete = () => {
            if (selectedRowKeys.length === 0) {
                notification.warning({ message: "Tidak ada data yang dipilih untuk dihapus.", duration: 4 });
                return;
            }
            const newDataSource = dataSource.filter(item => !selectedRowKeys.includes(item.key));
            setDataSource(newDataSource);
            setSelectedRowKeys([]);
            notification.success({ message: "Data yang dipilih berhasil dihapus.", duration: 4 });
        };

        const handleNew = () => {
            form.resetFields();
            setDataSource([]);
            form.setFieldsValue({ status: "Draft" });
            setStatusCode('1');
            setIsAutosaveActive(false);
            setAutosaveStatus('stopped');
            if (userData?.warehouse_code && warehouseData) {
                form.setFieldsValue({
                    warehouse: userData.warehouse_code,
                    dealer_rep_code: userData.warehouse_code,
                });
                handleWarehouseChange(userData.warehouse_code);
            }
        };

        const checkPoInGoodsReceive = async (poNo: string): Promise<boolean> => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/po/check-goods-receive/${poNo}`);
                return response.data.exists;
            } catch (error) {
                console.error("Gagal memeriksa Goods Receive:", error);
                return false;
            }
        };

        const handleVoid = async () => {
            const po_no = form.getFieldValue('po_no');
            if (!po_no) return;
            Modal.confirm({
                title: `Apakah anda yakin ingin membatalkan (Void) PO ${po_no}?`,
                content: 'Semua data pada database akan dihapus permanen.',
                okText: 'Ya, Void',
                okType: 'danger',
                onOk: async () => {
                    try {
                        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/po/void`, { po_no });
                        notification.success({ message: "Purchase Order berhasil di-void." });
                        handleNew();
                    } catch (error: any) {
                        notification.error({ message: error.response?.data?.error || "Gagal melakukan Void." });
                    }
                },
            });
        };

        const handleVoidApproval = async (type: 'Modify' | 'Cancel', remark: string) => {
            const values = form.getFieldsValue();
            try {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/po/void-request`, {
                    po_no: values.po_no,
                    warehouse_code: values.warehouse_code,
                    total: values.total_amount,
                    request_by: userData?.kode_grup1,
                    type: type,
                    remark: remark
                });
                notification.success({ message: "Permintaan Void berhasil dikirim." });
                handleNew();
            } catch (error: any) {
                notification.error({ message: "Gagal mengirim permintaan Void." });
            }
        };

        const terbilang = (n: number): string => {
            if (n < 0) return "minus " + terbilang(-n);
            if (n === 0) return "Nol";

            const satuan = ["", "satu", "dua", "tiga", "empat", "lima", "enam", "tujuh", "delapan", "sembilan"];
            const belasan = ["sepuluh", "sebelas", "dua belas", "tiga belas", "empat belas", "lima belas", "enam belas", "tujuh belas", "delapan belas", "sembilan belas"];
            const puluhan = ["", "", "dua puluh", "tiga puluh", "empat puluh", "lima puluh", "enam puluh", "tujuh puluh", "delapan puluh", "sembilan puluh"];
            const ribuan = ["", "ribu", "juta", "miliar", "triliun"];

            let result = "";
            let i = 0;

            while (n > 0) {
                const chunk = n % 1000;
                if (chunk > 0) {
                    let chunkStr = "";
                    const ratus = Math.floor(chunk / 100);
                    const sisa = chunk % 100;

                    if (ratus > 0) {
                        chunkStr += (ratus === 1 ? "seratus" : satuan[ratus] + " ratus");
                    }

                    if (sisa > 0) {
                        if (ratus > 0) chunkStr += " ";
                        if (sisa < 10) {
                            chunkStr += satuan[sisa];
                        } else if (sisa < 20) {
                            chunkStr += belasan[sisa - 10];
                        } else {
                            chunkStr += puluhan[Math.floor(sisa / 10)];
                            if (sisa % 10 > 0) {
                                chunkStr += " " + satuan[sisa % 10];
                            }
                        }
                    }
                    result = chunkStr + (i > 0 && chunkStr ? " " + ribuan[i] : "") + (result ? " " + result : "");
                }
                n = Math.floor(n / 1000);
                i++;
            }
            const finalResult = result.trim().replace(/\s+/g, ' ');
            return finalResult.charAt(0).toUpperCase() + finalResult.slice(1);
        };

        const handlePrint = async () => {
            const poNo = form.getFieldValue('po_no');
            if (!poNo) {
                notification.error({ message: "Tidak ada data Purchase Order untuk dicetak. Silakan muat data terlebih dahulu.", duration: 4 });
                return;
            }

            setIsPrinting(true);
            const key = 'print_progress_notification';
            const updateProgress = (percent: number) => {
                notification.info({
                    key,
                    message: 'Mempersiapkan Dokumen',
                    description: <Progress percent={percent} status="active" />,
                    duration: 0,
                    placement: 'topRight',
                });
            };

            try {
                updateProgress(0);
                const reportDataRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/po/report-data/${poNo}`);
                const { header, details } = reportDataRes.data;

                updateProgress(20);
                const duration = 4000;
                const interval = 100;
                const steps = duration / interval;
                const increment = (100 - 20) / steps;
                let currentPercent = 20;
                for (let i = 0; i < steps; i++) {
                    await new Promise(resolve => setTimeout(resolve, interval));
                    currentPercent += increment;
                    updateProgress(Math.round(currentPercent));
                }
                notification.destroy();

                const templateRes = await fetch('/reports/PO/index.html');
                let htmlContent = await templateRes.text();
                htmlContent = htmlContent.replace('<head>', '<head><base href="/reports/PO/">');

                const formatDate = (dateString: string | null) => {
                    if (!dateString) return '-';
                    const date = dayjs(dateString);
                    const months = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
                    return `${date.date()} ${months[date.month()]} ${date.year()}`;
                };

                const formatNumber = (num: number | null | undefined) => {
                    if (num === null || num === undefined) return '0';
                    return new Intl.NumberFormat('id-ID').format(Math.round(num));
                };

                const tableRows = details.map((item: any, index: number) => {
                    const price = parseFloat(item.price) || 0;
                    const qty = parseFloat(item.qty) || 0;
                    const discountAmount = parseFloat(item.discount) || 0;
                    const linePrice = price * qty;
                    const lineTotal = parseFloat(item.total) || 0;
                    const discountPercent = linePrice > 0 ? (discountAmount / linePrice) * 100 : 0;

                    return `
                        <tr>
                            <td style="text-align: center; padding: 5px;">${index + 1}</td>
                            <td style="text-align: left; padding: 5px;">${item.item_name || '-'}</td>
                            <td style="text-align: left; padding: 5px;">${item.item_code || '-'}</td>
                            <td style="text-align: right; padding: 5px;">${formatNumber(qty)}</td>
                            <td style="text-align: right; padding: 5px;">${item.uom || '-'}</td>
                            <td style="text-align: right; padding: 5px;">${formatNumber(price)}</td>
                            <td style="text-align: right; padding: 5px;">${formatNumber(linePrice)}</td>
                            <td style="text-align: right; padding: 5px;">${discountPercent.toFixed(2)}</td>
                            <td style="text-align: right; padding: 5px;">${formatNumber(discountAmount)}</td>
                            <td style="text-align: right; padding: 5px;">${formatNumber(linePrice)}</td>
                            <td style="text-align: right; padding: 5px;">${formatNumber(lineTotal)}</td>
                            <td style="text-align: left; padding: 5px;">${item.remark || '-'}</td>
                        </tr>
                    `;
                }).join('');

                const replacements = {
                    '{{FIELDS_OFFICE_NAME}}': header.office_name || '-',
                    '{{FIELDS_ADDRESS_OFFICE}}': header.office_address || '-',
                    '{{fields_supplier}}': header.supplier_name || '-',
                    '{{fields_address}}': header.supplier_address || '-',
                    '{{fields_delivery_to}}': header.warehouse_name || '-',
                    '{{fields_delivery_address}}': header.warehouse_address || '-',
                    '{{fields_contact_person}}': header.supplier_contact_person || '-',
                    '{{fields_phone_no}}': header.supplier_contact_person || '-',
                    '{{fields_doc_no}}': header.po_no || '-',
                    '{{fields_doc_date}}': formatDate(header.po_date),
                    '{{fields_order_type}}': header.order_type_desc || '-',
                    '{{fields_request_arrival_da}}': '-',
                    '{{fields_set_order}}': '-',
                    '{{fields_back_order}}': '-',
                    '{{fields_top}}': header.top_code_desc || '-',
                    '{{fields_sales_type}}': '-',
                    '{{fields_before_vat}}': formatNumber(header.sub_total),
                    '{{fields_vat}}': formatNumber(header.vat),
                    '{{fields_total}}': formatNumber(header.total),
                    '{{fields_in_words}}': `${terbilang(header.total || 0)} Rupiah`,
                    '{{fields_remark_bottom}}': header.remark || '-',
                    '{{print_date_sampit}}': formatDate(dayjs().toString()),
                };

                for (const [key, value] of Object.entries(replacements)) {
                    htmlContent = htmlContent.replace(new RegExp(key, 'g'), value as string);
                }

                const tbodyRegex = /<tbody>[\s\S]*?<\/tbody>/;
                htmlContent = htmlContent.replace(tbodyRegex, `<tbody>${tableRows}</tbody>`);

                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.write(htmlContent);
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => {
                        printWindow.print();
                    }, 500);
                }

            } catch (error) {
                console.error("Gagal mencetak Purchase Order:", error);
                notification.destroy();
                notification.error({ message: 'Gagal mempersiapkan data cetak.', key: 'printing', duration: 4 })
            } finally {
                setIsPrinting(false);
                notification.destroy(key);
            }
        };

        const handleSubmit = async () => {
            try {
                await form.validateFields();
                const po_no = form.getFieldValue('po_no');
                const dealer_rep_code = form.getFieldValue('dealer_rep_code');

                if (!po_no) {
                    notification.error({ message: "Nomor PO belum tersedia. Silakan pilih Tipe Order terlebih dahulu.", duration: 4 });
                    return;
                }

                let finalRemark = "Purchase Order ";

                Modal.confirm({
                    title: `Apakah anda yakin untuk melakukan Submit pada ${po_no} Ini?`,
                    content: (
                        <div>
                            <p style={{ marginBottom: "10px" }}>Silakan masukkan remark (catatan):</p>
                            <SubmitConfirmContent onChange={(val) => { finalRemark = val; }} />
                        </div>
                    ),
                    onOk: async () => {
                        try {
                            const totalAmount = form.getFieldValue('total_amount');
                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/po/submit`, {
                                po_no,
                                dealer_rep_code,
                                request_by: userData?.kode_grup1,
                                remark: finalRemark,
                                total: totalAmount
                            });
                            notification.success({ message: "Purchase Order telah berhasil disubmit untuk persetujuan.", duration: 4 });
                            form.resetFields();
                            setDataSource([]);
                            setSelectedRowKeys([]);
                            form.setFieldsValue({ status: "Draft" });
                            setIsAutosaveActive(false);
                            setAutosaveStatus('stopped');

                        } catch (axiosError: any) {
                            console.error("Gagal submit Purchase Order:", axiosError);
                            const errorMessage = axiosError.response?.data?.error || "Terjadi kesalahan saat submit Purchase Order.";
                            notification.error({ message: errorMessage, duration: 4 });
                        }
                    },
                    onCancel() {
                        console.log('Submit dibatalkan oleh pengguna.');
                    },
                });

            } catch (validationError: any) {
                console.log('Validasi Gagal:', validationError);
                notification.warning({ message: 'Silakan lengkapi semua field yang wajib diisi sebelum submit.', duration: 4 });
            }
        };

        const handleUseDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const isChecked = e.target.checked;
            const discountPercent = form.getFieldValue('discount') || 0;

            if (isChecked) {
                if (dataSource.length === 0) {
                    notification.warning({
                        message: 'Perhatian',
                        description: 'silahkan Copy Purchase Request terdahulu pada tabel',
                    });
                    form.setFieldsValue({ use_discount: false });
                    return;
                }

                Modal.confirm({
                    title: 'Konfirmasi',
                    content: 'Yakin menerapkan discount dari supplier yang telah di tetapkan secara global?',
                    okText: 'Ya',
                    cancelText: 'Batal',
                    onOk: () => {
                        const updatedDataSource = dataSource.map(item => {
                            const subTotal = item.price * item.qty;
                            const discountAmount = subTotal * (discountPercent / 100);
                            const total = subTotal - discountAmount;
                            return {
                                ...item,
                                discount: discountPercent,
                                discount_amount: discountAmount,
                                total: total,
                            };
                        });
                        setDataSource(updatedDataSource);
                        notification.success({ message: `Diskon sebesar ${discountPercent}% berhasil diterapkan pada semua item.`, duration: 4 });
                    },
                    onCancel: () => {
                        form.setFieldsValue({ use_discount: false });
                        console.log('Penerapan diskon dibatalkan.');
                    },
                });

            } else {
                const updatedDataSource = dataSource.map(item => {
                    const subTotal = item.price * item.qty;
                    return {
                        ...item,
                        discount: 0,
                        discount_amount: 0,
                        total: subTotal,
                    };
                });
                setDataSource(updatedDataSource);
                notification.info({ message: 'Diskon pada semua item telah dibatalkan.', duration: 4 });
            }
        };


        useEffect(() => {
            if (!poNo) {
                form.setFieldsValue({ status: "Draft" });
            }
        }, [form]);

        useEffect(() => {
            const calculateSummary = () => {
                if (dataSource.length === 0) {
                    form.setFieldsValue({
                        sub_total: 0,
                        total_discount: 0,
                        vat_amount: 0,
                        total_amount: 0,
                    });
                    return;
                }

                const subTotal = dataSource.reduce((sum, item) => sum + ((item.price || 0) * (item.qty || 0)), 0);
                const totalDiscount = dataSource.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
                const ppnRate = ppnData?.value_ppn || 0;
                const vatBase = subTotal - totalDiscount;
                const vatAmount = (vatBase * ppnRate) / 100;
                const totalAmount = subTotal - totalDiscount + vatAmount;

                form.setFieldsValue({
                    sub_total: subTotal,
                    total_discount: totalDiscount,
                    vat_amount: vatAmount,
                    total_amount: totalAmount,
                });
            };

            calculateSummary();
        }, [dataSource, form, ppnData]);

        useEffect(() => {
            if (brandError) {
                console.error('Gagal mengambil data brand:', brandError);
            }
        }, [brandError]);
        useEffect(() => {
            if (profitCenterError) {
                console.error('Gagal mengambil data profit center:', profitCenterError);
            }
        }, [profitCenterError]);
        useEffect(() => {
            if (orderTypeError) {
                console.error('Gagal mengambil data order type:', orderTypeError);
            }
        }, [orderTypeError]);
        useEffect(() => {
            if (topCodeError) {
                console.error('Gagal mengambil data TOP code:', topCodeError);
            }
        }, [topCodeError]);
        useEffect(() => {
            if (warehouseError) {
                console.error('Gagal mengambil data warehouse:', warehouseError);
            }
        }, [warehouseError]);

        const formatNumber = (value: number | undefined | null) => {
            if (value === null || value === undefined) return '0';
            return `${Math.round(value)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        };

        const columns: TableProps<DataType>["columns"] = [
            {
                title: "Pr No",
                dataIndex: "pr_no",
                key: "pr_no",
                render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text}</span>,
            },
            {
                title: "SO No",
                dataIndex: "so_no",
                key: "so_no",
            },
            {
                title: "Item Code",
                dataIndex: "item_code",
                key: "item_code",
            },
            {
                title: "Item Name",
                dataIndex: "item_name",
                key: "item_name",
            },
            {
                title: "UoM",
                dataIndex: "uom",
                key: "uom",
            },
            {
                title: "UoM Rate",
                dataIndex: "uom_rate",
                key: "uom_rate",
            },
            {
                title: "Qty",
                dataIndex: "qty",
                key: "qty",
            },
            {
                title: "Price",
                dataIndex: "price",
                key: "price",
                render: (text: number) => formatNumber(text),
            },
            {
                title: "Discount",
                dataIndex: "discount_amount",
                key: "discount",
                render: (text: number) => formatNumber(text),
            },
            {
                title: "Total",
                dataIndex: "total",
                key: "total",
                render: (text: number) => formatNumber(text),
            },
            {
                title: "Remark",
                dataIndex: "remark",
                key: "remark",
            },
        ];

        return (
            <WrappedComponent
                {...props}
                columns={columns}
                dataSource={dataSource}
                rowSelection={rowSelection}
                brandOptions={brandData || []}
                profitCenterOptions={profitCenterData || []}
                orderTypeOptions={orderTypeData || []}
                topCodeOptions={topCodeData || []}
                warehouseOptions={warehouseData || []}
                handleOrderTypeChange={handleOrderTypeChange}
                form={form}
                isModalVisible={isModalVisible}
                showModal={showModal}
                handleCancel={handleCancel}
                handleWarehouseChange={handleWarehouseChange}
                handleImport={handleImport}
                modalSelectedKeys={modalSelectedKeys}
                setModalSelectedKeys={setModalSelectedKeys}
                orderTypeForModal={orderTypeForModal}
                warehouseCodeForModal={warehouseCodeForModal}
                isSupplierModalVisible={isSupplierModalVisible}
                showSupplierModal={showSupplierModal}
                handleSupplierSelect={handleSupplierSelect}
                handleSupplierModalCancel={handleSupplierModalCancel}
                isEditModalVisible={isEditModalVisible}
                editingRecord={editingRecord}
                handleRowDoubleClick={handleRowDoubleClick}
                handleEditModalCancel={handleEditModalCancel}
                handleEditModalOk={handleEditModalOk}
                handleDelete={handleDelete}
                handleUseDiscountChange={handleUseDiscountChange}
                handleSubmit={handleSubmit}
                handleNew={handleNew}
                handleVoid={handleVoid}
                handleVoidApproval={handleVoidApproval}
                checkPoInGoodsReceive={checkPoInGoodsReceive}
                autosaveStatus={autosaveStatus}
                handlePrint={handlePrint}
                isPrinting={isPrinting}
                statusCode={statusCode}
                userData={userData}
            />
        );
    };

    return EnhancedComponent;
};

export default withPurchaseOrderLogic;